import { PredictionService } from '@/app/core/services/prediction/prediction.service';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  GetCustomerPredictionAction,
  SetSelectedClientsFilters,
  SetSelectedGatesFilters,
  SetSelectedGeneralFilters,
  SetMarketsFilter,
  SetClientFilter,
  SetGatesFilter,
  SetClientId,
  ResetFilters,
} from './customer-prediction.action';
import { EnovaAlertsComponent } from '@/app/core/utils/enova-alerts/enova-alerts.component';
import { catchError, tap, throwError } from 'rxjs';

export interface CustomerPredictionStateModel {
  customersData: any;
  selectedGeneralFilters: any[];
  selectedGatesFilters: any[];
  selectedClient: string;
  marketsFilter: any[];
  clientFilter: string | null;
  gatesFilter: any[];
  clientId: string | null;
}

@State<CustomerPredictionStateModel>({
  name: 'customerPrediction',
  defaults: {
    customersData: [],
    selectedGeneralFilters: [],
    selectedGatesFilters: [],
    selectedClient: '',
    marketsFilter: [],
    clientFilter: null,
    gatesFilter: [],
    clientId: null,
  },
})
@Injectable()
export class CustomerPredictionState {
  @Selector() static getCustomersData(
    state: CustomerPredictionStateModel
  ): any {
    return state?.customersData ? state.customersData : [];
  }

  @Selector() static getSelectedGeneralFilters(
    state: CustomerPredictionStateModel
  ): any {
    return state?.selectedGeneralFilters ? state.selectedGeneralFilters : [];
  }

  @Selector() static getSelectedGatesFilters(
    state: CustomerPredictionStateModel
  ): any {
    return state?.selectedGatesFilters ? state.selectedGatesFilters : [];
  }

  @Selector() static getSelectedClient(
    state: CustomerPredictionStateModel
  ): string {
    return state?.selectedClient ? state.selectedClient : '';
  }

  @Selector() static getMarketsFilter(
    state: CustomerPredictionStateModel
  ): any[] {
    return state?.marketsFilter ? state.marketsFilter : [];
  }

  @Selector() static getClientFilter(
    state: CustomerPredictionStateModel
  ): string | null {
    return state?.clientFilter ? state.clientFilter : null;
  }

  @Selector() static getGatesFilter(
    state: CustomerPredictionStateModel
  ): any[] {
    return state?.gatesFilter ? state.gatesFilter : [];
  }

  @Selector() static getClientId(
    state: CustomerPredictionStateModel
  ): string | null {
    return state?.clientId ? state.clientId : null;
  }

  constructor(
    private predictionService: PredictionService,
    public dialog: MatDialog
  ) {}

  @Action(GetCustomerPredictionAction)
  GetCustomerPredictionAction(
    ctx: StateContext<CustomerPredictionStateModel>,
    action: GetCustomerPredictionAction
  ) {
    const { startDate, endDate, filters } = action.payload;

    ctx.patchState({
      customersData: [],
    });
    return this.predictionService
      .getClientsForPrediction(startDate, endDate, filters)
      .pipe(
        tap((response: any) => {
          ctx.patchState({
            customersData: response,
          });
        }),
        catchError(error => {
          ctx.patchState({
            customersData: [],
          });
          this.dialog.open(EnovaAlertsComponent, {
            width: 'auto',
            data: {
              type: 'error',
              text:
                error?.error?.details ||
                error?.details ||
                error?.message ||
                'Ha ocurrido un error al obtener los datos',
              hiddeCancelBtn: true,
            },
          });
          return throwError(() => error);
        })
      );
  }

  @Action(SetSelectedGeneralFilters)
  setSelectedGeneralFilters(
    ctx: StateContext<CustomerPredictionStateModel>,
    action: SetSelectedGeneralFilters
  ) {
    ctx.patchState({
      selectedGeneralFilters: action.payload,
    });
  }
  @Action(SetSelectedGatesFilters)
  setSelectedGatesFilters(
    ctx: StateContext<CustomerPredictionStateModel>,
    action: SetSelectedGatesFilters
  ) {
    ctx.patchState({
      selectedGatesFilters: action.payload,
    });
  }

  @Action(SetSelectedClientsFilters)
  setSelectedClientsFilters(
    ctx: StateContext<CustomerPredictionStateModel>,
    action: SetSelectedClientsFilters
  ) {
    ctx.patchState({
      selectedClient: action.payload,
    });
  }

  @Action(SetMarketsFilter)
  setMarketsFilter(
    ctx: StateContext<CustomerPredictionStateModel>,
    action: SetMarketsFilter
  ) {
    ctx.patchState({
      marketsFilter: action.payload,
    });
  }

  @Action(SetClientFilter)
  setClientFilter(
    ctx: StateContext<CustomerPredictionStateModel>,
    action: SetClientFilter
  ) {
    ctx.patchState({
      clientFilter: action.payload,
    });
  }

  @Action(SetGatesFilter)
  setGatesFilter(
    ctx: StateContext<CustomerPredictionStateModel>,
    action: SetGatesFilter
  ) {
    ctx.patchState({
      gatesFilter: action.payload,
    });
  }

  @Action(SetClientId)
  setClientId(
    ctx: StateContext<CustomerPredictionStateModel>,
    action: SetClientId
  ) {
    ctx.patchState({
      clientId: action.payload,
    });
  }

  @Action(ResetFilters)
  resetFilters(ctx: StateContext<CustomerPredictionStateModel>) {
    ctx.patchState({
      marketsFilter: [],
      gatesFilter: [],
      clientFilter: null,
      clientId: null,
    });
  }
}
